import { useState, useEffect, useCallback, useRef } from 'react';
import {
  fetchCallRecords,
  getRecordCount,
  updateCallRecordStatus,
  deleteCallRecord,
  subscribeToTable,
  getCallRecordCategories,
  getCallRecordServiceTypes,
  TABLES,
} from '@/services/supabase';
import type { CallRecord, SortState, FilterState, PaginationState, RealtimePayload, DateFilter } from '@/types';
import { getFilterDateRange } from '@/utils';

interface UseRecordsOptions {
  tableName?: string;
  pageSize?: number;
  enableRealtime?: boolean;
}

interface UseRecordsReturn {
  records: CallRecord[];
  loading: boolean;
  error: string | null;
  pagination: PaginationState;
  filterState: FilterState;
  sortState: SortState;
  categories: string[];
  serviceTypes: string[];
  setFilterState: (state: Partial<FilterState>) => void;
  setSortState: (state: Partial<SortState>) => void;
  setPagination: (state: Partial<PaginationState>) => void;
  refresh: () => Promise<void>;
  toggleStatus: (id: string, status: boolean) => Promise<void>;
  deleteSelected: (ids: string[]) => Promise<void>;
  setTableName: (name: string) => void;
}

export function useRecords(options: UseRecordsOptions = {}): UseRecordsReturn {
  const { tableName: initialTableName = TABLES.CALL_RECORDS, pageSize = 25, enableRealtime = true } = options;

  const [records, setRecords] = useState<CallRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tableName, setTableNameState] = useState(initialTableName);
  
  // Update internal state when prop changes
  useEffect(() => {
    setTableNameState(initialTableName);
  }, [initialTableName]);
  
  const [pagination, setPaginationState] = useState<PaginationState>({ page: 0, pageSize, total: 0 });
  const [filterState, setFilterStateLocal] = useState<FilterState>({
    search: '',
    dateFilter: { preset: 'all', from: null, to: null },
    category: null,
    serviceType: null,
    status: null,
    urgency: null,
  });
  const [sortState, setSortStateLocal] = useState<SortState>({ column: 'created_at', direction: 'desc' });
  const [categories, setCategories] = useState<string[]>([]);
  const [serviceTypes, setServiceTypes] = useState<string[]>([]);

  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Fetch data with current filter state
  const fetchWithFilters = useCallback(async (currentFilterState: FilterState, currentPagination: PaginationState) => {
    setLoading(true);
    setError(null);

    const { from, to } = getFilterDateRange(currentFilterState.dateFilter);

    const [recordsResult, countResult] = await Promise.all([
      fetchCallRecords({
        tableName,
        page: currentPagination.page,
        pageSize: currentPagination.pageSize,
        search: currentFilterState.search,
        from: from || undefined,
        to: to || undefined,
        category: currentFilterState.category,
        serviceType: currentFilterState.serviceType,
        status: currentFilterState.status,
        urgency: currentFilterState.urgency,
        orderBy: sortState.column as keyof CallRecord,
        orderDir: sortState.direction,
      }),
      getRecordCount(tableName, { search: currentFilterState.search, from: from || undefined, to: to || undefined }),
    ]);

    if (recordsResult.error) {
      setError(recordsResult.error.message);
    } else {
      setRecords(recordsResult.data || []);
    }

    if (countResult.error) {
      console.error('Error fetching count:', countResult.error);
    } else {
      setPaginationState(prev => ({ ...prev, total: countResult.count }));
    }

    // Fetch filter options - only for alsalama_call_records table
    if (tableName === 'alsalama_call_records') {
      const [cats, types] = await Promise.all([
        getCallRecordCategories(tableName),
        getCallRecordServiceTypes(tableName),
      ]);
      setCategories(cats);
      setServiceTypes(types);
    } else {
      // For other tables, set empty arrays
      setCategories([]);
      setServiceTypes([]);
    }

    setLoading(false);
  }, [tableName, sortState]);

  // Fetch data
  const fetchData = useCallback(async () => {
    await fetchWithFilters(filterState, pagination);
  }, [filterState, pagination, fetchWithFilters]);

  // Initial load - only run once on mount
  useEffect(() => {
    fetchData();
  }, []); // Empty dependency - only run on mount

  // Watch for tableName changes and refetch data
  useEffect(() => {
    if (tableName) {
      setRecords([]);
      setPaginationState(p => ({ ...p, page: 0, total: 0 }));
      fetchData();
    }
  }, [tableName]);

  // Realtime subscription
  useEffect(() => {
    if (!enableRealtime) return;

    const handleRealtimeUpdate = (payload: RealtimePayload<CallRecord>) => {
      if (payload.eventType === 'INSERT') {
        setRecords(prev => [payload.new as CallRecord, ...prev]);
        setPaginationState(prev => ({ ...prev, total: prev.total + 1 }));
      } else if (payload.eventType === 'UPDATE') {
        setRecords(prev => prev.map((r) => (r.id === payload.new.id ? (payload.new as CallRecord) : r)));
      } else if (payload.eventType === 'DELETE') {
        setRecords(prev => prev.filter((r) => r.id !== payload.old.id));
        setPaginationState(prev => ({ ...prev, total: prev.total - 1 }));
      }
    };

    // Subscribe to the dynamic table name
    unsubscribeRef.current = subscribeToTable<CallRecord>(tableName, handleRealtimeUpdate);

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, [tableName, enableRealtime]);

  // Filter state handler
  const setFilterState = useCallback(async (state: Partial<FilterState>) => {
    // Calculate the new filter state first
    const newFilterState: FilterState = { ...filterState, ...state };
    
    // Update the state
    setFilterStateLocal(newFilterState);
    // Reset pagination when filters change
    setPaginationState(p => ({ ...p, page: 0 }));
    
    // Fetch data with the new filter state immediately
    await fetchWithFilters(newFilterState, { ...pagination, page: 0 });
  }, [filterState, pagination, fetchWithFilters]);

  // Sort state handler
  const setSortState = useCallback(async (state: Partial<SortState>) => {
    setSortStateLocal(prev => ({ ...prev, ...state }));
    await fetchData();
  }, [fetchData]);

  // Pagination state handler
  const setPagination = useCallback(async (state: Partial<PaginationState>) => {
    setPaginationState(prev => ({ ...prev, ...state }));
    await fetchData();
  }, [fetchData]);

  // Refresh handler
  const refresh = useCallback(async () => {
    setLoading(true);
    await fetchData();
    setLoading(false);
  }, [fetchData]);

  // Toggle status
  const toggleStatus = useCallback(async (id: string, status: boolean) => {
    const result = await updateCallRecordStatus(id, status, tableName);
    if (result.error) {
      throw result.error;
    }
    // Optimistic update
    setRecords(prev => prev.map((r) => (r.id === id ? { ...r, status } : r)));
  }, [tableName]);

  // Delete selected records
  const deleteSelected = useCallback(async (ids: string[]) => {
    const errors: Error[] = [];
    
    for (const id of ids) {
      const result = await deleteCallRecord(id, tableName);
      if (result.error) {
        errors.push(result.error);
      }
    }
    
    if (errors.length > 0) {
      throw errors[0];
    }
    
    // Optimistic update
    setRecords(prev => prev.filter((r) => !ids.includes(r.id)));
    setPaginationState(prev => ({ ...prev, total: prev.total - ids.length }));
  }, [tableName]);

  // Set table name
  const setTableName = useCallback((name: string) => {
    setTableNameState(name);
    setPaginationState(p => ({ ...p, page: 0, total: 0 }));
    setRecords([]);
  }, []);

  return {
    records,
    loading,
    error,
    pagination,
    filterState,
    sortState,
    categories,
    serviceTypes,
    setFilterState,
    setSortState,
    setPagination,
    refresh,
    toggleStatus,
    deleteSelected,
    setTableName,
  };
}

// Hook for analytics data
export function useAnalytics(tableName: string = TABLES.CALL_RECORDS, dateFilter?: DateFilter) {
  const [data, setData] = useState<{
    totalCalls: number;
    resolvedCalls: number;
    pendingCalls: number;
    avgDuration: number;
    byDate: Record<string, CallRecord[]>;
    byHour: Record<number, CallRecord[]>;
    byDayOfWeek: Record<number, CallRecord[]>;
    byCategory: Record<string, number>;
    byServiceType: Record<string, number>;
    durationRanges: Record<string, number>;
    
    // New analytics fields
    byUrgency: Record<string, number>;
    highUrgencyCount: number;
    highUrgencyRate: number;
    atRiskCalls: CallRecord[];
    atRiskCount: number;
    byUrgencyAndStatus: Record<string, { resolved: number; pending: number }>;
    serviceTypeEfficiency: Record<string, { total: number; resolved: number; pending: number; avgDuration: number; resolutionRate: number }>;
    peakHours: { hour: number; count: number }[];
    avgHandleTimeByCategory: Record<string, number>;
    avgHandleTimeByServiceType: Record<string, number>;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate date range from filter
      const { from, to } = getFilterDateRange(dateFilter || { preset: 'all', from: null, to: null });
      
      const { data: records, error: fetchError } = await fetchCallRecords({
        from: from || undefined,
        to: to || undefined,
      });

      if (fetchError) throw fetchError;

      const recordsArray = records || [];

      // Calculate metrics
      const totalCalls = recordsArray.length;
      const resolvedCalls = recordsArray.filter((r) => r.status === true).length;
      const pendingCalls = recordsArray.filter((r) => r.status === false).length;
      const avgDuration = totalCalls > 0
        ? Math.round(recordsArray.reduce((sum, r) => sum + (r.call_duration_sec || 0), 0) / totalCalls)
        : 0;

      // Group by date
      const byDate = recordsArray.reduce((acc, record) => {
        const date = record.created_at.split('T')[0];
        if (!acc[date]) acc[date] = [];
        acc[date].push(record);
        return acc;
      }, {} as Record<string, CallRecord[]>);

      // Group by hour
      const byHour = recordsArray.reduce((acc, record) => {
        const hour = new Date(record.created_at).getHours();
        if (!acc[hour]) acc[hour] = [];
        acc[hour].push(record);
        return acc;
      }, {} as Record<number, CallRecord[]>);

      // Group by day of week
      const byDayOfWeek = recordsArray.reduce((acc, record) => {
        const day = new Date(record.created_at).getDay();
        if (!acc[day]) acc[day] = [];
        acc[day].push(record);
        return acc;
      }, {} as Record<number, CallRecord[]>);

      // Group by category
      const byCategory = recordsArray.reduce((acc, record) => {
        const category = record.category || 'Unknown';
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Group by service type
      const byServiceType = recordsArray.reduce((acc, record) => {
        const type = record.service_type || 'Unknown';
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      // Duration ranges
      const durationRanges = {
        '0-30s': 0,
        '30-60s': 0,
        '1-2m': 0,
        '2-5m': 0,
        '5m+': 0,
      };
      recordsArray.forEach((record) => {
        const duration = record.call_duration_sec || 0;
        if (duration <= 30) durationRanges['0-30s']++;
        else if (duration <= 60) durationRanges['30-60s']++;
        else if (duration <= 120) durationRanges['1-2m']++;
        else if (duration <= 300) durationRanges['2-5m']++;
        else durationRanges['5m+']++;
      });

      // NEW: Urgency distribution
      const byUrgency: Record<string, number> = {
        low: 0,
        medium: 0,
        high: 0,
        extra_high: 0,
        no: 0,
      };
      recordsArray.forEach((record) => {
        const urgency = record.urgency || 'no';
        byUrgency[urgency] = (byUrgency[urgency] || 0) + 1;
      });

      // NEW: High urgency count and rate
      const highUrgencyCount = (byUrgency.high || 0) + (byUrgency.extra_high || 0);
      const highUrgencyRate = totalCalls > 0 ? Math.round((highUrgencyCount / totalCalls) * 100) : 0;

      // NEW: At-risk calls (high urgency, pending)
      const atRiskCalls = recordsArray.filter((r) => 
        (r.urgency === 'high' || r.urgency === 'extra_high') && r.status === false
      );
      const atRiskCount = atRiskCalls.length;

      // NEW: Urgency x Status cross-tabulation
      const byUrgencyAndStatus: Record<string, { resolved: number; pending: number }> = {};
      recordsArray.forEach((record) => {
        const urgency = record.urgency || 'no';
        if (!byUrgencyAndStatus[urgency]) {
          byUrgencyAndStatus[urgency] = { resolved: 0, pending: 0 };
        }
        if (record.status === true) {
          byUrgencyAndStatus[urgency].resolved++;
        } else {
          byUrgencyAndStatus[urgency].pending++;
        }
      });

      // NEW: Service type efficiency
      const serviceTypeEfficiency: Record<string, { total: number; resolved: number; pending: number; avgDuration: number; resolutionRate: number }> = {};
      const serviceTypeDurations: Record<string, { total: number; count: number }> = {};
      
      recordsArray.forEach((record) => {
        const type = record.service_type || 'Unknown';
        if (!serviceTypeEfficiency[type]) {
          serviceTypeEfficiency[type] = { total: 0, resolved: 0, pending: 0, avgDuration: 0, resolutionRate: 0 };
          serviceTypeDurations[type] = { total: 0, count: 0 };
        }
        serviceTypeEfficiency[type].total++;
        if (record.status === true) {
          serviceTypeEfficiency[type].resolved++;
        } else {
          serviceTypeEfficiency[type].pending++;
        }
        if (record.call_duration_sec) {
          serviceTypeDurations[type].total += record.call_duration_sec;
          serviceTypeDurations[type].count++;
        }
      });

      // Calculate average duration and resolution rate for each service type
      Object.keys(serviceTypeEfficiency).forEach((type) => {
        const efficiency = serviceTypeEfficiency[type];
        efficiency.avgDuration = serviceTypeDurations[type].count > 0
          ? Math.round(serviceTypeDurations[type].total / serviceTypeDurations[type].count)
          : 0;
        efficiency.resolutionRate = efficiency.total > 0
          ? Math.round((efficiency.resolved / efficiency.total) * 100)
          : 0;
      });

      // NEW: Peak hours (sorted by count descending)
      const peakHours = Object.entries(byHour)
        .map(([hour, records]) => ({
          hour: parseInt(hour),
          count: records.length,
        }))
        .sort((a, b) => b.count - a.count);

      // NEW: Average handle time by category
      const avgHandleTimeByCategory: Record<string, number> = {};
      const categoryDurations: Record<string, { total: number; count: number }> = {};
      
      recordsArray.forEach((record) => {
        const category = record.category || 'Unknown';
        if (!categoryDurations[category]) {
          categoryDurations[category] = { total: 0, count: 0 };
        }
        if (record.call_duration_sec) {
          categoryDurations[category].total += record.call_duration_sec;
          categoryDurations[category].count++;
        }
      });

      Object.keys(categoryDurations).forEach((category) => {
        avgHandleTimeByCategory[category] = categoryDurations[category].count > 0
          ? Math.round(categoryDurations[category].total / categoryDurations[category].count)
          : 0;
      });

      // NEW: Average handle time by service type
      const avgHandleTimeByServiceType: Record<string, number> = {};
      Object.keys(serviceTypeDurations).forEach((type) => {
        avgHandleTimeByServiceType[type] = serviceTypeDurations[type].count > 0
          ? Math.round(serviceTypeDurations[type].total / serviceTypeDurations[type].count)
          : 0;
      });

      setData({
        totalCalls,
        resolvedCalls,
        pendingCalls,
        avgDuration,
        byDate,
        byHour,
        byDayOfWeek,
        byCategory,
        byServiceType,
        durationRanges,
        
        // New analytics data
        byUrgency,
        highUrgencyCount,
        highUrgencyRate,
        atRiskCalls,
        atRiskCount,
        byUrgencyAndStatus,
        serviceTypeEfficiency,
        peakHours,
        avgHandleTimeByCategory,
        avgHandleTimeByServiceType,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, [tableName, dateFilter]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return { data, loading, error, refresh: fetchAnalytics };
}

// Hook for local storage
export function useLocalStorage<T>(key: string, defaultValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch {
      return defaultValue;
    }
  });

  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [key, storedValue]);

  return [storedValue, setValue] as const;
}

// Hook for keyboard shortcuts
export function useKeyboardShortcut(key: string, callback: () => void, modifiers: { ctrl?: boolean; shift?: boolean; alt?: boolean } = {}) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { ctrl = false, shift = false, alt = false } = modifiers;

      if (
        event.key.toLowerCase() === key.toLowerCase() &&
        event.ctrlKey === ctrl &&
        event.shiftKey === shift &&
        event.altKey === alt
      ) {
        event.preventDefault();
        callback();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [key, callback, modifiers]);
}

// Hook for window size
export function useWindowSize() {
  const [size, setSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return size;
}
